/**
 * AdvancedTable 列配置 → AGGridTable 列配置转换
 */
import type { TableColumnConfig } from '@/types/advanced-table'
import type { AGGridColumnConfig } from '@/types/ag-grid-table'

/**
 * 将单列 AdvancedTable 配置转为 AGGridColumnConfig（递归处理分组子列）
 *
 * @param config AdvancedTable 列配置
 * @param forceHidden 父列隐藏时强制隐藏子列（对齐 AdvancedTable 行为）
 * @returns AGGrid 列配置
 */
export const convertTableColumnToAGGrid = (
  config: TableColumnConfig,
  forceHidden = false
): AGGridColumnConfig => {
  const hasChildren = Array.isArray(config.children) && config.children.length > 0
  const field = config.dataIndex || config.key || ''
  const visible = forceHidden ? false : config.visible !== false
  // 与 AdvancedTable getAllColumnsConfig 一致：顶层 fixed 优先，否则回落 originalConfig.fixed
  const pinned = config.fixed || (config.originalConfig as { fixed?: 'left' | 'right' } | undefined)?.fixed
  // Arco 表体默认左对齐；未声明 align 时不沿用 AGGrid 全局居中
  const align = config.align || 'left'

  const result: AGGridColumnConfig = {
    key: config.key || field,
    field: hasChildren ? undefined : field,
    headerName: config.title,
    width: config.width,
    minWidth: config.minWidth,
    visible,
    order: config.order,
    pinned,
    sortable: config.sortable === true,
    filter: false,
    floatingFilter: false,
    resizable: config.resizable !== false,
    // 操作列统一用 action，避免与 AGGridTable 工具栏 #actions 插槽冲突
    slotName: config.slotName === 'actions' ? 'action' : config.slotName,
    cellStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent:
        align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
      textAlign: align
    }
  }

  if (hasChildren) {
    result.marryChildren = true
    // 父列不可见时，子列一并隐藏，避免 AG Grid 把隐藏分组的子列摊平出来
    const hideChildren = forceHidden || config.visible === false
    result.children = config.children!.map(child => convertTableColumnToAGGrid(child, hideChildren))
  }

  return result
}

/**
 * 批量转换 AdvancedTable 列配置为 AGGrid 列配置
 *
 * @param configs AdvancedTable 列配置列表
 * @returns AGGrid 列配置列表
 */
export const convertTableColumnsToAGGrid = (
  configs: TableColumnConfig[]
): AGGridColumnConfig[] => {
  if (!Array.isArray(configs)) return []
  return configs
    .filter(config => config && typeof config === 'object')
    .map(config => convertTableColumnToAGGrid(config))
    .sort((a, b) => (a.order || 0) - (b.order || 0))
}
